import { useEffect, useState } from "react";
import { MessageCircle, ChevronDown, ShieldCheck, Loader2, BadgeCheck, X } from "lucide-react";
import { api } from "@/lib/api";

const fmtDuration = (s) => {
  if (!s || s <= 0) return "—";
  const m = Math.floor(s / 60);
  const sec = Math.round(s - m * 60);
  if (m === 0) return `${sec}s`;
  return `${m}m ${sec}s`;
};

const fmtJoined = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      month: "short", year: "numeric",
    });
  } catch { return "—"; }
};

const Verified = ({ ok, label }) => (
  <span
    className={
      "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold " +
      (ok ? "bg-up/10 text-up" : "bg-surface-2 text-muted-foreground")
    }
    data-testid={`cp-verify-${label.toLowerCase()}`}
  >
    {ok ? <BadgeCheck size={11} /> : null}
    {label}
  </span>
);

/**
 * CounterpartyCard
 * Compact card shown on the order page summarising the OTHER party.
 * Click → expands an inline modal with the full profile + KuCoin-style stats.
 */
export default function CounterpartyCard({ orderId, counterpartyId, counterpartyEmail, onChatClick }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!counterpartyId) { setLoading(false); return; }
    let live = true;
    (async () => {
      try {
        const r = await api.get(`/api/p2p/users/${counterpartyId}/profile`);
        if (live) setProfile(r.data);
      } catch (e) {
        if (live) setError(e.response?.data?.detail || e.message);
      } finally { if (live) setLoading(false); }
    })();
    return () => { live = false; };
  }, [counterpartyId]);

  const masked = profile?.nickname || maskEmail(counterpartyEmail);
  const initial = (masked || "?").trim().charAt(0).toUpperCase();

  return (
    <>
      <div
        className="rounded-lg border border-border bg-card px-4 py-3"
        data-testid="counterparty-card"
      >
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-full grid place-items-center text-white font-semibold text-[15px] flex-shrink-0"
            style={{ background: "linear-gradient(120deg, oklch(var(--brand-emerald)), oklch(var(--brand-lime)))" }}
            data-testid="cp-avatar"
          >
            {initial}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 flex-wrap">
              <button
                onClick={() => setExpanded(true)}
                className="text-foreground font-semibold text-[14px] hover:underline truncate text-left"
                data-testid="cp-name"
              >
                {masked}
              </button>
              {profile?.is_merchant && (
                <ShieldCheck size={13} className="text-primary" aria-label="Verified merchant" />
              )}
            </div>
            <div className="text-[11.5px] text-muted-foreground flex items-center gap-2 flex-wrap mt-0.5">
              {profile?.country && <span>{profile.country}</span>}
              {profile && (
                <>
                  <span className="opacity-50">·</span>
                  <span>
                    {profile.trades_total} trades · {Number(profile.total_completion_rate).toFixed(0)}% completion
                  </span>
                </>
              )}
              {loading && <Loader2 size={11} className="animate-spin opacity-60" />}
              {error && <span className="text-down">{error}</span>}
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button
              onClick={() => setExpanded(true)}
              className="px-2.5 py-1.5 rounded-md border border-border text-muted-foreground hover:text-foreground text-[12px] font-semibold inline-flex items-center gap-1"
              data-testid="cp-view-profile"
              aria-label="View full profile"
            >
              Profile <ChevronDown size={11} />
            </button>
            {onChatClick && (
              <button
                onClick={onChatClick}
                className="px-2.5 py-1.5 rounded-md brand-gradient text-primary-foreground text-[12px] font-semibold inline-flex items-center gap-1"
                data-testid="cp-chat"
              >
                <MessageCircle size={11} /> Chat
              </button>
            )}
          </div>
        </div>
      </div>

      {expanded && (
        <ProfileModal
          profile={profile}
          masked={masked}
          loading={loading}
          error={error}
          onClose={() => setExpanded(false)}
        />
      )}
    </>
  );
}

function ProfileModal({ profile, masked, loading, error, onClose }) {
  return (
    <div
      className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="cp-profile-modal"
    >
      <div
        className="bg-card rounded-2xl border border-border w-full max-w-[480px] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <h3 className="text-[15px] font-semibold text-foreground">Counterparty Profile</h3>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
            aria-label="Close"
            data-testid="cp-modal-close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="flex items-center gap-3">
            <div
              className="w-14 h-14 rounded-full grid place-items-center text-white font-semibold text-[20px]"
              style={{ background: "linear-gradient(120deg, oklch(var(--brand-emerald)), oklch(var(--brand-lime)))" }}
            >
              {(masked || "?").trim().charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="text-foreground font-semibold text-[16px] truncate flex items-center gap-1.5">
                {masked}
                {profile?.is_merchant && (
                  <ShieldCheck size={14} className="text-primary" />
                )}
              </div>
              <div className="text-[12px] text-muted-foreground">
                {profile?.country || "India"}
                {profile?.joined_at && <> · Joined {fmtJoined(profile.joined_at)}</>}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-1.5">
            <Verified ok={profile?.kyc_verified} label="KYC" />
            <Verified ok={profile?.email_verified} label="Email" />
            <Verified ok={profile?.phone_verified} label="Phone" />
          </div>

          {loading && (
            <div className="text-muted-foreground text-[12.5px] inline-flex items-center gap-2 py-3">
              <Loader2 size={13} className="animate-spin" /> Loading stats…
            </div>
          )}
          {error && !loading && (
            <div className="text-down bg-down/10 rounded-lg p-3 text-[12.5px]">
              {error}
            </div>
          )}

          {profile && !loading && (
            <>
              <div className="grid grid-cols-2 gap-2.5">
                <Stat label="Trades (30d)" value={profile.trades_30d ?? 0} />
                <Stat
                  label="Completion (30d)"
                  value={`${Number(profile.completion_rate_30d).toFixed(2)}%`}
                />
                <Stat label="Avg. Payment Time" value={fmtDuration(profile.avg_payment_time_seconds)} />
                <Stat label="Avg. Release Time" value={fmtDuration(profile.avg_release_time_seconds)} />
              </div>

              <div className="border-t border-border pt-3 grid grid-cols-2 gap-2.5">
                <Stat label="Total Trades" value={profile.trades_total ?? 0} highlight />
                <Stat
                  label="Total Completion"
                  value={`${Number(profile.total_completion_rate).toFixed(2)}%`}
                  highlight
                />
              </div>

              {(profile.positive_ratings || profile.negative_ratings) ? (
                <div className="text-[12px] text-muted-foreground">
                  Ratings: <span className="text-up font-semibold">{profile.positive_ratings} 👍</span>
                  {" · "}
                  <span className="text-down font-semibold">{profile.negative_ratings} 👎</span>
                </div>
              ) : null}
            </>
          )}
        </div>

        <div className="px-5 py-4 border-t border-border bg-surface-2 text-[11.5px] text-muted-foreground">
          Public profile — only shown to traders you've matched with on this platform.
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div
      className={
        "rounded-lg border p-3 " +
        (highlight ? "bg-surface-2 border-border" : "border-border bg-card")
      }
    >
      <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
      <div className="text-[16px] font-semibold text-foreground mt-1 tabular-nums">{value}</div>
    </div>
  );
}

function maskEmail(email) {
  if (!email) return "Trader";
  const [local, domain] = email.split("@");
  if (!domain) return email;
  const head = (local || "").slice(0, 3);
  return `${head}***@${domain}`;
}
